import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import DeleteConfirmModal from '../../components/admin/DeleteConfirmModal';
import { Size } from '../../types';
import './AdminSizes.css';

interface SizeFormData {
  name: string;
  width: string;
  height: string;
  price: string;
}

const AdminSizes: React.FC = () => {
  const [sizes, setSizes] = useState<Size[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; id: number | null }>({ show: false, id: null });
  const [formData, setFormData] = useState<SizeFormData>({
    name: '',
    width: '',
    height: '',
    price: ''
  });
  const { success, error: showError } = useToast();

  const fetchSizes = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get<Size[]>('/admin/sizes');
      setSizes(response.data);
    } catch (error) {
      console.error('Failed to fetch sizes:', error);
      showError('Failed to load sizes');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchSizes();
  }, [fetchSizes]);

  const saveSize = async (): Promise<void> => {
    if (!formData.name.trim() || !formData.width || !formData.height || !formData.price) {
      showError('Please fill in all size fields.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        price: parseFloat(formData.price)
      };

      if (editingId) {
        await api.put(`/admin/sizes/${editingId}`, payload);
        success('Size updated');
      } else {
        await api.post('/admin/sizes', payload);
        success('Size created');
      }

      handleCloseModal();
      fetchSizes();
    } catch (error) {
      console.error('Failed to save size:', error);
      showError('Failed to save size');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    void saveSize();
  };

  const handleEdit = (size: Size): void => {
    setEditingId(size.id);
    setFormData({
      name: size.name,
      width: size.width.toString(),
      height: size.height.toString(),
      price: size.price.toString()
    });
    setShowModal(true);
  };

  const handleDelete = (id: number): void => {
    setDeleteConfirm({ show: true, id });
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleteConfirm.id) return;
    try {
      setDeleting(true);
      await api.delete(`/admin/sizes/${deleteConfirm.id}`);
      success('Size deleted');
      fetchSizes();
      setDeleteConfirm({ show: false, id: null });
    } catch (error) {
      console.error('Failed to delete size:', error);
      showError('Failed to delete size');
    } finally {
      setDeleting(false);
    }
  };

  const resetForm = (): void => {
    setFormData({
      name: '',
      width: '',
      height: '',
      price: ''
    });
    setEditingId(null);
  };

  const handleCloseModal = (): void => {
    setShowModal(false);
    resetForm();
  };

  // Calculate ratio for display
  const calculatedRatio = formData.width && formData.height 
    ? (parseFloat(formData.width) / parseFloat(formData.height)).toFixed(4)
    : '0.0000';

  if (loading) {
    return <div className="loading">Loading sizes...</div>;
  }

  return (
    <div className="admin-sizes">
      <div className="container">
        <div className="page-header">
          <div className="page-header-left">
            <Link to="/admin" className="back-button">
              <i className="fas fa-arrow-left"></i> Back to Admin Panel
            </Link>
            <h1>Size Management</h1>
          </div>
          <button onClick={() => { setEditingId(null); resetForm(); setShowModal(true); }} className="btn btn-primary">
            <i className="fas fa-plus"></i> Add Size
          </button>
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <form onSubmit={handleSubmit} className="size-form" noValidate>
                <h2>{editingId ? 'Edit' : 'Add'} Size</h2>
                
                <div className="form-group">
                  <label>Name (e.g., "5x10", "11x14")</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="5x10"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Width (inches)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.width}
                      onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                      required
                      placeholder="5"
                    />
                  </div>

                  <div className="form-group">
                    <label>Height (inches)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      required
                      placeholder="10"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Base Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    placeholder="25.00"
                  />
                </div>

                <div className="form-group">
                  <label>Calculated Ratio</label>
                  <input
                    type="text"
                    value={calculatedRatio}
                    readOnly
                    className="readonly-input"
                  />
                  <small>Ratio is automatically calculated: Width ÷ Height</small>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={() => void saveSize()} disabled={saving}>
                    {saving && <span className="btn-spinner" aria-hidden="true"></span>}
                    {saving ? 'Saving...' : `${editingId ? 'Update' : 'Create'} Size`}
                  </button>
                  <button type="button" onClick={handleCloseModal} className="btn btn-outline">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmModal
          isOpen={deleteConfirm.show}
          onClose={() => setDeleteConfirm({ show: false, id: null })}
          onConfirm={confirmDelete}
          loading={deleting}
          title="Delete size?"
          message="Are you sure you want to delete this size? This may affect aspect ratios that use it."
        />

        <div className="sizes-grid">
          {sizes.map((size) => (
            <div key={size.id} className="size-card">
              <div className="size-header">
                <h3>{size.name}</h3>
                <span className="ratio-value">Ratio: {parseFloat(size.ratio).toFixed(4)}</span>
              </div>
              <div className="size-details">
                <div className="size-detail-item">
                  <span className="label">Dimensions:</span>
                  <span className="value">{size.width}" × {size.height}"</span>
                </div>
                <div className="size-detail-item">
                  <span className="label">Base Price:</span>
                  <span className="value price">${size.price.toFixed(2)}</span>
                </div>
              </div>
              <div className="size-actions">
                <button 
                  onClick={() => handleEdit(size)} 
                  className="btn-icon"
                  title="Edit size"
                  aria-label="Edit size"
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button 
                  onClick={() => handleDelete(size.id)} 
                  className="btn-icon btn-icon-danger"
                  title="Delete size"
                  aria-label="Delete size"
                >
                  <i className="fas fa-trash"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminSizes;
